<template>
  <v-card>
    <v-card-title>
      {{ title }}
    </v-card-title>
    <v-card-text>
      <v-container class="pa-0">
        <v-row>
          <div v-if="isMultiCurrency && !form.parentMilestoneId">
            <v-checkbox
              v-model="form.multiCurrencyMilestone"
              :disabled="!!payment.id"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">{{ $t('payment.milestone.multiCurrencyTitle') }}</div>
                  <div class="text--secondary">{{ $t('payment.milestone.multiCurrencySubtitle') }}</div>
                </div>
              </template>
            </v-checkbox>
          </div>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.name"
              :label="$t('payment.milestone.name')"
              counter="32"
              dense
              outlined
              @change="$v.form.name.$touch()"
              :error-messages="[...errorMessages('name'), nameDuplicateError ].filter(i => !!i)"
            />
          </v-col>
          <v-col>
            <VfPercentageInput
              v-model="inputPercent"
              :label="label"
              dense
              outlined
              :inputValue="inputValue"
              :outputValue.sync="calculatedValue"
              :hint="percentHint"
              :disabled="isPercentDisabled"
              @change="handlePercentChange"
              @focus="handlePercentFocus"
              :error-messages="[...errorMessages('paymentAmountCents'), percentOutOfBoundsError].filter(i => !!i)"
            />
          </v-col>
          <v-col>
            <VfCurrencyInput
              v-model="form.paymentAmountCents"
              dense
              outlined
              :currency="currency"
              @change="handleAmountChange"
              @focus="handleAmountFocus"
              :error-messages="errorMessages('paymentAmountCents')"
              :label="$t('payment.milestone.payment')"
              :disabled="isCurrencyInputDisabled"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              v-model="form.description"
              :label="$t('common.description')"
              dense
              outlined
              counter="128"
              rows="2"
              @change="$v.form.description.$touch()"
              :error-messages="errorMessages('description')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <PartsSelect
              v-model="form.part"
              :parts="partsList"
              :disabled="isPartsDisabled"
              :isMultiCurrency="isMultiCurrency"
              @input="v => form.part = v"
            />
          </v-col>
          <v-col>
            <v-autocomplete
              v-model="form.purchaseOrder"
              :label="$t('part.orderNumber')"
              :items="purchaseOrderNumberList"
              dense
              outlined
              @change="$v.form.purchaseOrder.$touch()"
              :error-messages="errorMessages('purchaseOrder')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <vf-date-input
              v-model="form.originalDate"
              :label="$t('payment.milestone.originalDate')"
              outlined
              dense
              @change="$v.form.originalDate.$touch()"
              @input="v => form.forecastedDate = form.forecastedDate || v"
              :error-messages="errorMessages('originalDate')"
            />
          </v-col>
          <v-col>
            <vf-date-input
              v-model="form.forecastedDate"
              :label="$t('payment.milestone.forecastedDate')"
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <vf-date-input
              v-model="form.expectedInvoiceDate"
              :label="$t('payment.milestone.expectedInvoiceDate')"
              outlined
              dense
            />
          </v-col>
          <v-col>
            <vf-date-input
              v-model="form.expectedPaymentDate"
              :label="$t('payment.milestone.expectedPaymentDate')"
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.comment"
              :label="$t('payment.milestone.substantiation')"
              dense
              outlined
              counter="120"
              @change="$v.form.comment.$touch()"
              :error-messages="errorMessages('comment')"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        rounded
        dark
        dense
        outlined
        color="primary"
        @click="cancel"
      >
        {{ $t('common.cancel') }}
      </v-btn>
      <SubmitButton @click="submit" />
    </v-card-actions>
  </v-card>
</template>

<script>
import { errorMessageGenerator, milestoneModelForm } from '@/validators'
import { EMPTY_PAYMENT_MILESTONE, toFixedWithoutRounding } from '@/helpers'
import { getLocalizationContext } from '@/helpers/translations'
import VfPercentageInput from '../common/VfPercentageInput.vue'

export default {
  components: { VfPercentageInput },
  name: 'PaymentMilestoneForm',
  props: {
    title: String,
    payment: {
      type: Object,
      default: () => ({})
    },
    maxAmountsPerPart: Object,
    maxAmountsPerCurrency: Object,
    contractPrice: [Object, Number],
    percentHint: String,
    currencies: Array,
    isVariation: Boolean,
    parts: {
      type: Array,
      default: () => ([])
    },
    checkNameDuplicate: Function,
    isMultiCurrency: Boolean,
    parentMilestone: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      key: 0,
      originalDateMenu: false,
      forecastedDateMenu: false,
      form: this.preparePayment(this.payment),
      calculatedValue: 0,
      maxAmountWhileEditing: 0,
      inputPercent: 0,
      editPaymentMode: !!this.payment.id,
      percentMode: true
    }
  },
  created () {
    this.$eventBus.$on('CLICK_OUTSIDE_MODAL', () => {
      this.cancel()
    })
  },
  mounted () {
    if (this.editPaymentMode) {
      this.maxAmountWhileEditing = this.form.part.id
        ? this.form.paymentAmountCents + this.partRemainingValue
        : this.form.paymentAmountCents + this.maxAmountsPerCurrency[this.currency.key || this.currency]
    }
    this.updateInitialPercentValue()
  },
  computed: {
    currency () {
      return (this.form && this.form.part && this.form.part.currency)
        ? this.currencies.find(i => i.key === this.form.part.currency)
        : this.currencies.find(i => i.value === 1)
    },
    inputValue () {
      if ((this.isMultiCurrency || this.form.part.id) && !this.isSubMilestone) {
        return this.form.part.signedPriceCents
      }
      if (this.isMultiCurrency && this.isSubMilestone) {
        return this.contractPrice[this.currency.key || this.currency]
      }

      return this.contractPrice.sum ? this.contractPrice.sum : this.contractPrice
    },
    nameDuplicateError () {
      return this.checkNameDuplicate(this.form) ? this.$t('payment.milestone.isDuplicated') : null
    },
    percentOutOfBoundsError () {
      const boundsError = !(this.inputPercent >= 0.00 && this.inputPercent <= 1.00)
      return boundsError ? this.$t('payment.milestone.percentOutOfBounds') : null
    },
    partsList () {
      const init = this.parts.length === 1
        ? []
        : (this.payment.id && this.payment.subMilestonesWithDifferentParts
            ? [{ name: this.$t(getLocalizationContext(this.isMultiCurrency) + '.multiple'), id: null }]
            : [{ name: this.$t(getLocalizationContext(this.isMultiCurrency) + '.all'), id: null, disabled: this.isMultiCurrency }])
      return [...init, ...this.parts]
    },
    percentOfMilestone () {
      return this.form.percentOfMilestone
    },
    partialEdit () {
      return !!this.form.amountAssignedToApplication && !!this.form.amountAssignedToApplication.sum
    },
    label () {
      if (this.isVariation) return this.$t('payment.milestone.percentOfVariation')
      if (this.form.parentMilestoneId) return this.$t('payment.milestone.percentOfMilestone')
      if (this.isMultiCurrency) return this.$t('payment.milestone.percentOfPart')
      if (this.form.part.id) return this.$t('payment.milestone.percentOfSite')
      return this.$t('payment.milestone.percentOfContract')
    },
    isPercentDisabled () {
      return this.partialEdit || !!this.payment.variationId || !!this.payment.liquidatedDamageId || this.form.multiCurrencyMilestone
    },
    isCurrencyInputDisabled () {
      return this.partialEdit || !!this.payment.variationId || !!this.payment.liquidatedDamageId || this.form.multiCurrencyMilestone
    },
    isPartsDisabled () {
      return this.form.multiCurrencyMilestone || this.partialEdit || !!this.parentMilestone.partId || !!this.payment.variationId || !!this.payment.liquidatedDamageId
    },
    isSubMilestone () {
      return !!this.form.parentMilestoneId
    },
    partRemainingValue () {
      return Number(toFixedWithoutRounding(this.form.part.remainingValue, 0))
    },
    purchaseOrderNumberList () {
      return this.parts.map(i => i.purchaseOrder).filter(i => i)
    }
  },
  methods: {
    preparePayment (payment) {
      const newPayment = { ...payment } || EMPTY_PAYMENT_MILESTONE
      newPayment.multiCurrencyMilestone = this.isMultiCurrency && payment.multiCurrencyMilestone

      const currency = newPayment.currency || (newPayment.part && newPayment.part.currencyData) || this.currencies.find(i => i.value === 1)

      const part = newPayment.partId
        ? this.parts.find(i => i.id === newPayment.partId) || { name: this.$t(getLocalizationContext(this.isMultiCurrency) + '.all'), id: null }
        : newPayment.id
          ? { name: newPayment.subMilestonesWithDifferentParts ? this.$t(getLocalizationContext(this.isMultiCurrency) + '.multiple') : this.$t(getLocalizationContext(this.isMultiCurrency) + '.all'), id: null }
          : (newPayment.multiCurrencyMilestone || this.isMultiCurrency || this.parts.length === 1) ? this.parts[0] : { name: this.$t(getLocalizationContext(this.isMultiCurrency) + '.all'), id: null }

      const maxPercent = part.id
        ? ((this.maxAmountsPerPart[part.id] / part.signedPriceCents) * 100)
        : ((this.maxAmountsPerCurrency[currency.key] / this.contractPrice[currency.key]) * 100)
      const maxAmount = part.id ? this.maxAmountsPerPart[part.id] : this.maxAmountsPerCurrency[currency.key]

      newPayment.paymentAmountCents = this.payment.id
        ? (payment.multiCurrencyMilestone ? null : payment.paymentAmountCents) // when it have multiple currencies
        : maxAmount
      if (newPayment.variationId || newPayment.liquidatedDamageId) {
        newPayment.percent = 0
      } else {
        if (newPayment.id && !this.payment.parentMilestoneId) {
          newPayment.percent = this.isMultiCurrency || part.id
            ? payment.paymentAmountCents / part.signedPriceCents * 100
            : newPayment.percentOfMilestone || payment.percentOfContractPrice
        } else if (newPayment.id && this.payment.parentMilestoneId) {
          newPayment.percent = payment.paymentAmountCents / this.contractPrice * 100
        } else {
          newPayment.percent = maxPercent
        }
      }

      newPayment.purchaseOrder = newPayment.purchaseOrder || ''
      newPayment.partId = newPayment.partId || part.id
      newPayment.part = part
      return newPayment
    },
    errorMessages (key) {
      return errorMessageGenerator(this.$v.form[key], key)
    },
    cancel () {
      this.$v.$reset()
      this.$eventBus.$emit('CLOSE_MODAL')
      this.form = this.preparePayment(this.payment)
    },
    async submit () {
      await this.$v.form.$touch()
      if (!this.$v.form.$invalid && !this.nameDuplicateError && !this.percentOutOfBoundsError) {
        if (!this.form.id) {
          this.form.forecastedDate = this.form.originalDate
        } else {
          this.form.percentOfMilestone
            ? (this.form.percentOfMilestone = Number(this.form.percent))
            : (this.form.percentOfContractPrice = Number(this.form.percent))
        }
        this.form.amountAssignedToApplication =
          this.form.amountAssignedToApplication || 0
        this.form.amountPaid = this.form.amountPaid || 0
        this.form.partId = this.form.part.id
        if (this.payment.variationId || this.payment.liquidatedDamageId) {
          this.form.paymentAmountCents = this.payment.paymentAmountCents
          this.form.percentOfContractPrice = this.payment.percentOfContractPrice
        }
        if (this.form.multiCurrencyMilestone) {
          this.$emit('mandatorySubmilestones', {
            ...this.form,
            paymentAmountCents: this.payment.paymentAmountCents
          })
        } else {
          this.$emit('savePayment', { ...this.form })
          this.$v.$reset()
          this.form = this.preparePayment(this.payment)
          this.$eventBus.$emit('CLOSE_MODAL')
        }
      }
    },
    calculateMaxAmountsForMultiCurrencyContract (part) {
      const amountCents = this.editPaymentMode
        ? this.maxAmountWhileEditing
        : part
          ? this.maxAmountsPerPart[part.id]
          : this.maxAmountsPerCurrency[this.currency.key]
      const percent = part
        ? (this.editPaymentMode
            ? this.maxAmountWhileEditing / this.form.part.signedPriceCents * 100
            : ((this.maxAmountsPerPart[part.id] / part.signedPriceCents) * 100)
          )
        : ((this.maxAmountsPerCurrency[this.currency.key] / this.contractPrice[this.currency.key]) * 100)

      return {
        amountCents,
        percent
      }
    },
    calculateMaxAmountsForSingleCurrencyContract (part) {
      const amountCents = this.editPaymentMode
        ? this.maxAmountWhileEditing
        : (part
            ? this.partRemainingValue
            : this.maxAmountsPerCurrency[this.currency.key || this.currency]
          )
      const percent = part ? amountCents / this.form.part.signedPriceCents * 100 : (amountCents / this.contractPrice.sum) * 100

      return {
        amountCents,
        percent
      }
    },
    calculateMaxAmountsForSubMilestone () {
      const amountCents = this.maxAmountsPerCurrency[this.currency.key || this.currency] + this.payment.paymentAmountCents
      const percent = amountCents / this.contractPrice * 100

      return {
        amountCents,
        percent
      }
    },
    async calculatePriceAndPercent (options = { updatePercent: false, updateAmount: false }) {
      const part = this.form.partId ? this.parts.find(i => i.id === this.form.partId) : null
      let maxAmount = 0
      let maxPercent = 0
      await this.updateOnlyPercentValue()
      if (!this.payment.liquidatedDamageId && !this.payment.variationId) {
        // Multicurrency logic
        if (this.form.percent !== null && this.isMultiCurrency && !this.multiCurrencyMilestone && !this.isSubMilestone) {
          const { percent, amountCents } = this.calculateMaxAmountsForMultiCurrencyContract(part)
          maxPercent = percent
          maxAmount = amountCents

          if (this.percentMode) {
            this.form.paymentAmountCents = part
              ? this.calculatedValue > maxAmount
                ? maxAmount
                : this.calculatedValue
              : this.contractPrice[this.currency.key] * (Number(this.form.percent) / 100)
          } else {
            this.form.paymentAmountCents = part
              ? this.form.paymentAmountCents > maxAmount
                ? maxAmount
                : this.form.paymentAmountCents
              : this.contractPrice[this.currency.key] * (Number(this.form.percent) / 100)
          }
        }

        // Single currency logic
        if (!this.isMultiCurrency && !this.isSubMilestone) {
          const { percent, amountCents } = this.calculateMaxAmountsForSingleCurrencyContract(part)
          maxPercent = percent
          maxAmount = amountCents

          if (Number(this.form.percent) > maxPercent) {
            this.form.percent = maxPercent
            this.key = this.key + 1
          } else if (this.percentMode) {
            this.form.paymentAmountCents = this.calculatedValue > maxAmount
              ? maxAmount
              : this.calculatedValue
          } else {
            this.form.paymentAmountCents = this.form.paymentAmountCents > maxAmount
              ? maxAmount
              : this.form.paymentAmountCents
          }
        }

        // SubMilestone logic
        if (this.isSubMilestone) {
          const { percent, amountCents } = this.calculateMaxAmountsForSubMilestone(part)
          maxPercent = percent
          maxAmount = amountCents

          if (Number(this.form.percent) > maxPercent) {
            this.form.percent = maxPercent
            this.key = this.key + 1
          } else if (this.percentMode) {
            this.form.paymentAmountCents = this.calculatedValue > maxAmount
              ? maxAmount
              : this.calculatedValue
          } else {
            this.form.paymentAmountCents = this.form.paymentAmountCents > maxAmount
              ? maxAmount
              : this.form.paymentAmountCents
          }
        }
      }

      if (options.updateAmount) {
        this.form.paymentAmountCents = maxAmount
      }

      if (options.updatePercent) {
        this.form.percent = maxPercent
      }
    },
    calculatePercent () {
      let percent
      if (this.isSubMilestone) {
        percent = this.form.paymentAmountCents / this.contractPrice * 100
      } else {
        percent = this.form.paymentAmountCents / this.form.part.signedPriceCents * 100
      }

      this.form.percent = percent
    },
    updateInitialPercentValue () {
      this.$nextTick(() => {
        this.calculatePriceAndPercent()
        this.inputPercent = this.form.percent / 100
      })
    },
    async updateOnlyPercentValue () {
      await this.$nextTick(() => {
        this.inputPercent = this.form.percent / 100
      })
    },
    handlePercentChange (value) {
      this.$v.form.paymentAmountCents.$touch()
    },
    handleAmountChange (value) {
      this.$v.form.paymentAmountCents.$touch()
    },
    handleAmountFocus () {
      this.percentMode = false
    },
    handlePercentFocus () {
      this.percentMode = true
    }
  },
  watch: {
    'form.multiCurrencyMilestone' (v, p) {
      if (v && !p) {
        this.form.partId = null
        this.form.part = { name: this.$t(getLocalizationContext(this.isMultiCurrency) + '.multiple') }
        this.form.paymentAmountCents = null
        this.form.percent = null
      } else {
        const part = this.payment.partId
          ? this.parts.find(i => i.id === this.payment.partId) || { name: this.$t(getLocalizationContext(this.isMultiCurrency) + '.all'), partId: null }
          : this.payment.id
            ? { name: this.payment.subMilestonesWithDifferentParts ? this.$t(getLocalizationContext(this.isMultiCurrency) + '.multiple') : this.$t(getLocalizationContext(this.isMultiCurrency) + '.all'), partId: null }
            : (this.payment.multiCurrencyMilestone || this.isMultiCurrency || this.parts.length === 1) ? this.parts[0] : { name: this.$t(getLocalizationContext(this.isMultiCurrency) + '.all') }
        const maxPercent = part
          ? ((this.maxAmountsPerPart[part.id] / part.signedPriceCents) * 100)
          : ((this.maxAmountsPerCurrency[this.currency.key] / this.contractPrice[this.currency.key]) * 100)
        this.form.partId = part.id
        this.form.part = part
        this.form.paymentAmountCents = this.maxAmountsPerPart[part.id]
        this.form.percent = maxPercent
      }
    },
    async 'form.part' (v, p) {
      if (v.id !== p.id) {
        const orderList = this.parts.map(i => i.purchaseOrder).filter(i => i)
        if (!this.payment.id && (this.form.purchaseOrder === p.purchaseOrder || this.form.purchaseOrder === orderList[0] || !this.form.purchaseOrder)) {
          this.form.purchaseOrder = v.purchaseOrder
        }
        this.form.partId = v.id
        const remainingValue = v.remainingValue > 0 ? Number(toFixedWithoutRounding(v.remainingValue, 0)) : 0

        if (v.id === this.payment.partId) {
          this.maxAmountWhileEditing = v.id
            ? this.payment.paymentAmountCents + remainingValue
            : this.payment.paymentAmountCents + this.maxAmountsPerCurrency[this.currency.key || this.currency]
        } else if (!this.payment.partId && v.id) {
          this.maxAmountWhileEditing = remainingValue + (this.payment.paymentAmountCents * v.ratio / 100)
        } else {
          this.maxAmountWhileEditing = v.id ? remainingValue : this.payment.paymentAmountCents + this.maxAmountsPerCurrency[this.currency.key || this.currency]
        }
        if (!this.form.multiCurrencyMilestone) {
          this.calculatePriceAndPercent({ updatePercent: true })
          await this.updateOnlyPercentValue()
        }
      }
    },
    payment (v) {
      this.form = this.preparePayment(v)
    },
    'form.percent' (v) {
      this.calculatePriceAndPercent()
    },
    'form.paymentAmountCents' (v) {
      if (!this.percentMode) {
        this.calculatePercent()
      }
    },
    inputPercent (v) {
      this.form.percent = v * 100
    }
  },
  validations: milestoneModelForm
}
</script>
